import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ResourceTable } from "@/components/data/ResourceTable";
import { PageHeader } from "@/components/data/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const FIELDS = [
  ["nom_entreprise", "Raison sociale"], ["sigle", "Sigle"], ["adresse", "Adresse"], ["telephone", "Téléphone"],
  ["email", "Email"], ["devise", "Code devise (ex. GNF)"], ["symbole_devise", "Symbole affiché"],
  ["decimales", "Nombre de décimales"],
  ["taux_commission_revendeur", "Commission revendeur (% du CA de son TPE)"], ["taux_commission_superviseur", "Commission superviseur (% du CA de son réseau)"],
  ["taux_commission_kgd", "Commission KGD (% du CA total)"], ["delai_paiement_commissions", "Délai de paiement des commissions (jours)"],
  ["partenaire_nom", "Nom du partenaire"], ["solde_initial_partenaire", "Solde initial dette partenaire"],
  ["date_solde_initial_partenaire", "Date du solde initial partenaire (AAAA-MM-JJ)"],
  ["taux_frais_mobile_money", "Frais Mobile Money spécial → banque (%)"], ["delai_alerte_echeance", "Délai d'alerte des échéances de charges (jours)"],
  ["pied_de_page_rapports", "Pied de page des rapports PDF"],
];

function GeneralSettings() {
  const { meta, loadMeta, can } = useAuth();
  const [f, setF] = useState(meta.settings || {});
  useEffect(() => {
    setF(meta.settings || {});
  }, [meta.settings]);
  const save = async () => {
    try {
      await api.put("/settings", f);
      await loadMeta();
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const editable = can("parametres", "parametrer");
  return (
    <div className="bg-card border rounded-lg p-4 sm:p-6 max-w-3xl">
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map(([k, l]) => (
          <div key={k}><Label className="text-xs font-semibold">{l}</Label>
            <Input className="mt-1" disabled={!editable} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`settings-${k}`} />
          </div>
        ))}
        <div className="flex items-center gap-3 sm:col-span-2">
          <Switch disabled={!editable} checked={f.partenaire_deduit_superviseur !== false} onCheckedChange={(v) => setF({ ...f, partenaire_deduit_superviseur: v })} data-testid="settings-partenaire-sup" />
          <Label>Dette partenaire : déduire la commission superviseur</Label>
        </div>
        <div className="flex items-center gap-3 sm:col-span-2">
          <Switch disabled={!editable} checked={f.partenaire_deduit_kgd !== false} onCheckedChange={(v) => setF({ ...f, partenaire_deduit_kgd: v })} data-testid="settings-partenaire-kgd" />
          <Label>Dette partenaire : déduire la commission KGD</Label>
        </div>
        <div className="flex items-center gap-3 sm:col-span-2">
          <Switch disabled={!editable} checked={!!f.autoriser_solde_negatif} onCheckedChange={(v) => setF({ ...f, autoriser_solde_negatif: v })} data-testid="settings-solde-negatif" />
          <Label>Autoriser un solde négatif sur les comptes financiers</Label>
        </div>
      </div>
      {editable && <Button className="mt-6" onClick={save} data-testid="settings-save-button">Enregistrer</Button>}
    </div>
  );
}

export default function ParametresPage() {
  const { meta } = useAuth();
  return (
    <div>
      <PageHeader title="Paramètres généraux" subtitle="Société, devise, taux de commission et listes paramétrables. Modification réservée aux rôles ayant « Gérer les paramètres »." />
      <Tabs defaultValue="general">
        <TabsList className="mb-4 flex-wrap h-auto justify-start">
          <TabsTrigger value="general" data-testid="tab-general">Général & taux</TabsTrigger>
          {meta.ref_types.map((t) => <TabsTrigger key={t.value} value={t.value} data-testid={`tab-ref-${t.value}`}>{t.label}</TabsTrigger>)}
          <TabsTrigger value="comptes" data-testid="tab-comptes">Comptes de trésorerie</TabsTrigger>
        </TabsList>
        <TabsContent value="general"><GeneralSettings /></TabsContent>
        <TabsContent value="comptes"><ResourceTable res="comptes" /></TabsContent>
        {meta.ref_types.map((t) => (
          <TabsContent key={t.value} value={t.value}>
            <ResourceTable res="refs" fixedValues={{ type: t.value }} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
